// src/components/LearnerBenefitsCard.jsx
'use client';

import { useState, useEffect, useRef } from 'react';
import Image from 'next/image';

export default function LearnerBenefitsCard() {
  const [count, setCount] = useState(0);
  const countRef = useRef(null);
  const targetCount = 6.5; // Target is 5.4M+

  // Generate array of 12 learner images
  const learnerImages = Array.from({ length: 12 }, (_, i) => 
    `https://d31bwppm8yl9g2.cloudfront.net/learner/website/Learne-${i + 1}.svg`
  );

  useEffect(() => {
    // Counter animation from 0 to 5.4
    const duration = 2000; // 2 seconds duration
    const startTime = Date.now();
    
    const animateCount = () => {
      const elapsedTime = Date.now() - startTime;
      const progress = Math.min(elapsedTime / duration, 1);
      
      // Easing function for smoother animation
      const easeOutQuad = progress => 1 - (1 - progress) * (1 - progress);
      const easedProgress = easeOutQuad(progress);
      
      setCount(easedProgress * targetCount);
      
      if (progress < 1) {
        requestAnimationFrame(animateCount);
      }
    };
    
    animateCount();
  }, []);

  return (
    <div className="bg-white rounded-2xl border border-[rgba(0,39,38,0.4)] p-6 overflow-hidden">
      {/* Header with Learner Count */}
      <div className="mb-6">
        <h3 className="text-[#002726] text-xl font-medium">
          {count.toFixed(1)}M+ Learners
        </h3>
        <p className="text-[#002726] text-sm">have reaped benefits from our programs</p>
      </div>
      
      {/* Infinite Marquee of Learner Images */}
      <div className="mb-8 overflow-hidden">
        <div className="marquee-container">
          <div className="marquee-track">
            {/* First set of images */}
            {learnerImages.map((image, index) => (
              <div key={`first-${index}`} className="flex flex-col items-center mx-2">
                <div className="relative w-16 h-auto rounded-sm overflow-hidden mb-2 border border-gray-100">
                  <Image 
                    src={image} 
                    alt={`Learner ${index + 1}`}
                    width={64}
                    height={64}
                    className="object-cover"
                  />
                </div>
              </div>
            ))}
            
            {/* Duplicate set for seamless looping */}
            {learnerImages.map((image, index) => (
              <div key={`second-${index}`} className="flex flex-col items-center mx-2">
                <div className="relative w-16 h-auto overflow-hidden mb-2 border border-gray-100">
                  <Image 
                    src={image} 
                    alt={`Learner ${index + 1}`}
                    width={64}
                    height={64}
                    className="object-cover"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Benefits List - Static */}
      <div className="space-y-4 mb-8">
        <div className="flex items-start">
          <div className="flex-shrink-0 mt-1">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7 13L10 16L17 9" stroke="#4CAF50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="12" cy="12" r="10" stroke="#4CAF50" strokeWidth="2"/>
            </svg>
          </div>
          <p className="ml-3 text-[#002726] text-sm">
            Stay ahead in your field by mastering industry relevant skills through our online sessions
          </p>
        </div>
        
        <div className="flex items-start">
          <div className="flex-shrink-0 mt-1">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7 13L10 16L17 9" stroke="#4CAF50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="12" cy="12" r="10" stroke="#4CAF50" strokeWidth="2"/>
            </svg>
          </div>
          <p className="ml-3 text-[#002726] text-sm">
            Dive into real challenges from today's businesses, gaining hands-on experience.
          </p>
        </div>
        
        <div className="flex items-start">
          <div className="flex-shrink-0 mt-1">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7 13L10 16L17 9" stroke="#4CAF50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="12" cy="12" r="10" stroke="#4CAF50" strokeWidth="2"/>
            </svg>
          </div>
          <p className="ml-3 text-[#002726] text-sm">
            Tap into a wealth of career opportunities through our established network.
          </p>
        </div>
      </div>
      
      {/* Company Logo - Fixed width */}
      <div className="flex flex-col md:flex-row items-center justify-between pt-4 border-t border-gray-200">
        <div className="relative h-full w-full mb-4 md:mb-0">
          <Image 
            src="https://d31bwppm8yl9g2.cloudfront.net/learner/website/outlink.svg"
            alt="Growth School Logo" 
            width={260}
            height={68}
            className="object-contain"
          />
        </div>
      </div>

      {/* Include inline styles for the marquee */}
      <style jsx>{`
        .marquee-container {
          width: 100%;
          overflow: hidden;
        }
        
        .marquee-track {
          display: flex;
          animation: marquee 30s linear infinite;
          width: max-content;
        }
        
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  );
}